import {DocumentNode, gql} from '@apollo/client';
import {groupBy} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {TypedValue} from 'notebook/tables/TableConstants';
import {sortNodesByCoordinates} from 'procedures/design/flowchartManagementUtils';
import {collapseTypedValuesForMultiValuePlateMap} from 'procedures/planning/multiValuePlateMap';
import {
  ExperimentalInputs,
  ExperimentalInputTypedValuesByComponentName,
} from 'procedures/planning/ProcedurePlanTypes';
import {getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {
  EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT,
  ExperimentFlowchart,
  ExperimentMethod,
  getValuesForMethod,
  prepareInitialExperimentalInputs,
} from 'studies/typed/shared/ExperimentalInputs';
import {EXPERIMENTAL_INPUTS_PROCEDURE_FLOWCHART_VERSION_PROXY_FRAGMENT} from 'studies/typed/shared/ExperimentalInputs';
import {ConditionGroup} from 'studies/typed/shared/useExperimentDesignState';

import {GetStudyDesignFromProcedureRunPlanGraphQL as ProcedureRunPlanNode} from './__generated/GetStudyDesignFromProcedureRunPlanGraphQL';

export interface StudyDesignParams {
  experimentalInputs: ExperimentalInputs;
  templateFlowchart: ExperimentFlowchart;
  conditionGroups: Array<ConditionGroup>;
}

/**
 * Extracts the study design from a ProcedureRunPlanNode.
 *
 * @param procedureRunPlan - a ProcedureRunPlanNode that contains STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT
 * @returns an object of {experimentalInputs, templateFlowchart} for use with design components.
 */
export default function getStudyDesignFromProcedureRunPlan(
  procedureRunPlan: ProcedureRunPlanNode
): StudyDesignParams {
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(procedureRunPlan);
  assert(flowchartVersion != null, 'Expected flowchart version to be present');
  assert(!isInaccessible(flowchartVersion), 'Expected flowchart version to be accessible');

  const nodes = flowchartVersion.nodes;
  const templateMethods: Array<ExperimentMethod> = sortNodesByCoordinates(
    [...nodes],
    'gridLayoutCoordinates'
  );

  const templateFlowchart: ExperimentFlowchart = {
    ...flowchartVersion,
    nodes: templateMethods,
  };

  const sortedConditions = [...procedureRunPlan.procedureRunPlanConditions].sort(
    (a, b) => a.position - b.position
  );
  let conditionGroups: Array<ConditionGroup>;
  // When the plan has no stored conditions, fall back to a single empty "Condition 1" so downstream code
  // always has at least one condition to render. It shouldn't normally
  // be hit since we prevent deleting the last condition in the UI and always create one by default when creating
  // the plan.
  if (sortedConditions.length === 0) {
    conditionGroups = [
      {
        conditionIdsByPmdvId: {},
        numReplicates: 1,
        name: 'Condition 1',
        description: undefined,
      },
    ];
  } else {
    conditionGroups = buildConditionGroupsForPerPmdvConditions(sortedConditions);
  }

  const numConditions = conditionGroups.length;

  const confirmationMethodsByParamVersionId = new Map(
    procedureRunPlan.confirmationMethodByParamVersionId.map((cm) => [
      cm.paramVersionId,
      cm.confirmationMethod,
    ])
  );

  const prevConfirmationMethods = Object.fromEntries(
    templateMethods.map((methodNode) => [
      methodNode.id,
      {
        parameterConfirmationMethods: getConfirmationMethodsForMethod(
          methodNode,
          confirmationMethodsByParamVersionId
        ),
      },
    ])
  );

  // Build planValues from procedureRunPlanConditions.methodParametersPlan
  // For each parameter, if no value was stored (empty array), use the template default.
  // This ensures "No change" is shown correctly when values equal the default.
  const planValues = Object.fromEntries(
    templateMethods.map((methodNode) => {
      const defaultValues = getValuesForMethod(methodNode);
      return [
        methodNode.id,
        {
          values: conditionGroups.map((conditionGroup) => {
            const conditionByPmdvId = sortedConditions.find(
              (condition) => condition.id === conditionGroup.conditionIdsByPmdvId[methodNode.id]
            );
            // For unsaved plans (no persisted conditions yet), there may be no stored condition row.
            if (conditionByPmdvId == null) {
              return defaultValues;
            }
            const methodPlan = conditionByPmdvId.methodParametersPlan;
            const loadedValues = getValuesFromMethodPlan(methodNode, methodPlan);
            return mergeValuesWithDefaults(loadedValues, defaultValues);
          }),
        },
      ];
    })
  );

  const experimentalInputs = prepareInitialExperimentalInputs(numConditions, templateMethods, {
    prevExperimentalInputValues: planValues,
    prevConfirmationMethods,
  });

  return {experimentalInputs, templateFlowchart, conditionGroups};
}

type MethodPlan = ProcedureRunPlanNode['procedureRunPlanConditions'][number]['methodParametersPlan'];

type PlanCondition = ProcedureRunPlanNode['procedureRunPlanConditions'][number];

/**
 * Collapses per-PMDV `ProcedureRunPlanCondition` rows into UI-level {@link ConditionGroup}s.
 *
 * In the per-PMDV plan model, each logical condition is stored as one row per PMDV (i.e. per
 * template method version) at a shared `position`. This function groups those rows by `position`
 * and produces one `ConditionGroup` per position, where:
 *   - `conditionIdsByPmdvId` maps each PMDV id to the id of the corresponding `ProcedureRunPlanCondition` row.
 *     Every row is guaranteed to have a `templateMethodVersion` (it is non-nullable).
 *   - `name`, `description`, and `numReplicates` are taken from a canonical row at the position
 *     (the first one in iteration order); these fields are expected to be consistent across all
 *     PMDV rows at the same position.
 *
 * Assumes `sortedConditions` is already sorted by `position`.
 */
function buildConditionGroupsForPerPmdvConditions(
  sortedConditions: Array<PlanCondition>
): Array<ConditionGroup> {
  const conditionsByPosition = groupBy(sortedConditions, (condition) => condition.position);
  const positions = Object.keys(conditionsByPosition)
    .map((position) => Number(position))
    .sort((a, b) => a - b);

  return positions.map((position) => {
    const conditionsAtPosition = conditionsByPosition[position];
    const canonicalCondition = conditionsAtPosition[0];
    const conditionIdsByPmdvId = Object.fromEntries(
      conditionsAtPosition.map((condition) => [
        getAccessibleOrInaccessibleId(condition.templateMethodVersion),
        condition.id,
      ])
    );

    return {
      conditionIdsByPmdvId,
      numReplicates: canonicalCondition.replicates.length > 0 ? canonicalCondition.replicates.length : 1,
      name: canonicalCondition.name,
      description: canonicalCondition.description ?? undefined,
    };
  });
}

/**
 * Merges loaded values with defaults at the parameter level.
 * For each parameter, if the loaded value is an empty array (no stored value), use the default instead.
 * This ensures "No change" is shown correctly when values equal the template default.
 */
function mergeValuesWithDefaults(
  loadedValues: ExperimentalInputTypedValuesByComponentName,
  defaultValues: ExperimentalInputTypedValuesByComponentName
): ExperimentalInputTypedValuesByComponentName {
  return {
    parameters: loadedValues.parameters.map((paramFields, paramIdx) =>
      paramFields.length > 0 ? paramFields : defaultValues.parameters[paramIdx]
    ),
    materialInputs: loadedValues.materialInputs.map((inputFields, inputIdx) =>
      inputFields.length > 0 ? inputFields : defaultValues.materialInputs[inputIdx]
    ),
    equipment: loadedValues.equipment.map((equipFields, equipIdx) =>
      equipFields.length > 0 ? equipFields : defaultValues.equipment[equipIdx]
    ),
  };
}

/**
 * Extracts parameter values from a method plan, matching them to the template method's parameter order.
 * Similar to getValuesForMethod but for plan stored values instead of PMEI planned values.
 *
 * Plan values are stored as JSONScalar with different shapes:
 * - Data parameters: { value: TypedValue }
 * - Material inputs: { value: { material: TypedValue, amount: TypedValue } }
 * - Equipment: { value: { equipment: TypedValue } }
 *
 * For plate-confirmed parameters with multiple setpoints, the plan may contain multiple stored
 * entries sharing a single `parameterId` / `materialInputId`. Those are collapsed back into a
 * single multi-value TypedValue (ARRAY or multi-link BIOENTITY_LINK) for the UI, matching the
 * shape `useInputTable` expects for `isMulti: true` columns.
 */
function getValuesFromMethodPlan(
  templateMethod: ExperimentMethod,
  methodPlan: MethodPlan
): ExperimentalInputTypedValuesByComponentName {
  if (methodPlan == null) {
    return {
      parameters: templateMethod.dataParameterSettings.map(() => []),
      materialInputs: templateMethod.materialInputSettings.map(() => []),
      equipment: templateMethod.equipmentSettings.map(() => []),
    };
  }

  const dataParamValuesById = groupBy(methodPlan.dataParameterValues, (r) => r.parameterId);
  const materialInputValuesById = groupBy(methodPlan.materialInputValues, (r) => r.materialInputId);
  const equipmentValuesById = groupBy(methodPlan.equipmentValues, (r) => r.equipmentId);

  return {
    parameters: templateMethod.dataParameterSettings.map((setting) => {
      const storedEntries = dataParamValuesById[setting.version.id] ?? [];
      const perEntryValues = storedEntries.map(
        (row) => (row.value as {value: TypedValue} | null)?.value ?? null
      );
      const collapsed = collapseTypedValuesForMultiValuePlateMap(perEntryValues);
      return collapsed != null ? [collapsed] : [];
    }),

    materialInputs: templateMethod.materialInputSettings.map((setting) => {
      const storedEntries = materialInputValuesById[setting.version.id] ?? [];
      const innerValues = storedEntries.map(
        (row) =>
          (row.value as {value: {material: TypedValue | null; amount: TypedValue | null}} | null)?.value
      );
      const perEntryMaterials = innerValues.map((iv) => iv?.material ?? null);
      const collapsedMaterial = collapseTypedValuesForMultiValuePlateMap(perEntryMaterials);
      const amount = innerValues.map((iv) => iv?.amount ?? null).find((v) => v != null) ?? null;
      if (collapsedMaterial == null && amount == null) {
        return [];
      }
      return [collapsedMaterial, amount] as Array<TypedValue>;
    }),

    equipment: templateMethod.equipmentSettings.map((setting) => {
      const storedEntries = equipmentValuesById[setting.version.id] ?? [];
      assert(
        storedEntries.length <= 1,
        `Expected at most one stored equipment entry for ${setting.version.id}, got ${storedEntries.length}`
      );
      const value =
        (storedEntries[0]?.value as {value: {equipment: TypedValue | null}} | null)?.value?.equipment ?? null;
      return value != null ? [value] : [];
    }),
  };
}

/**
 * Gets confirmation methods for a method's parameters based on the plan-level mapping.
 */
function getConfirmationMethodsForMethod(
  methodNode: ExperimentMethod,
  confirmationMethodsByParamVersionId: Map<string, ProcedureParameterConfirmationMethod>
): ExperimentalInputs[string]['parameterConfirmationMethods'] {
  return {
    parameters: methodNode.dataParameterSettings.map(
      (setting) =>
        confirmationMethodsByParamVersionId.get(setting.version.id) ?? setting.version.confirmationMethod
    ),
    materialInputs: methodNode.materialInputSettings.map(
      (setting) =>
        confirmationMethodsByParamVersionId.get(setting.version.id) ?? setting.version.confirmationMethod
    ),
    equipment: methodNode.equipmentSettings.map(
      (setting) =>
        confirmationMethodsByParamVersionId.get(setting.version.id) ?? setting.version.confirmationMethod
    ),
  };
}

export const STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT: DocumentNode = gql`
  fragment GetStudyDesignFromProcedureRunPlanGraphQL on ProcedureRunPlanNode {
    id
    description
    dataEntryMode
    templateProcedureFlowchartVersion {
      ... on ProcedureFlowchartVersionNode {
        id
        ...ExperimentalInputsFlowchartGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    procedureFlowchartConfigVersionProxy {
      ...ExperimentalInputsProcedureFlowchartVersionProxyGraphQL
      ...InaccessibleInfoGraphQL
    }
    procedureRunPlanConditions {
      id
      name
      description
      position
      templateMethodVersion {
        ... on ProcedureMethodDefinitionVersionNode {
          id
        }
        ... on InaccessibleInfoNode {
          ...InaccessibleInfoGraphQL
        }
      }
      replicates {
        replicateNumber
      }
      methodParametersPlan {
        templateMethodVersion {
          id
        }
        dataParameterValues {
          parameterId
          value
        }
        materialInputValues {
          materialInputId
          value
        }
        equipmentValues {
          equipmentId
          value
        }
      }
    }
    confirmationMethodByParamVersionId {
      paramVersionId
      confirmationMethod
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT}
  ${EXPERIMENTAL_INPUTS_PROCEDURE_FLOWCHART_VERSION_PROXY_FRAGMENT}
`;
